/**
 * usePageModals 훅 - URL 기반 페이지 + ModalsContext 통합
 *
 * URL로 관리되는 페이지:
 * - /login, /verify/email, /verify/birthday
 * - /profile, /order-history, /payment-method, /privacy-settings
 * - /announcements, /terms, /privacy
 *
 * ModalsContext로 관리되는 모달 (URL 변경 없음):
 * - MapModal, SettingsModal, LogoutConfirm
 */
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useModals } from '@/contexts/ModalsContext';
import { logger } from '@/utils/logger';

export function usePageModals() {
  const location = useLocation();
  const { navigate, goBack } = useSafeNavigate();

  // ModalsContext에서 모달 상태 가져오기
  const {
    showMapModal,
    showSettingsModal,
    showLogoutConfirm,
    openMapModal,
    closeMapModal,
    openSettingsModal,
    closeSettingsModal,
    openLogoutConfirm,
    closeLogoutConfirm,
    setShowMapModal,
    setShowSettingsModal,
    setShowLogoutConfirm,
    closeAllModals,
  } = useModals();

  // URL 기반 페이지 상태 (파생)
  const showLoginPage = location.pathname === ROUTES.LOGIN;
  const showEmailVerification = location.pathname === ROUTES.VERIFY_EMAIL;
  const showBirthdayVerification = location.pathname === ROUTES.VERIFY_BIRTHDAY;
  const showProfilePage = location.pathname === ROUTES.PROFILE;
  const showOrderHistory = location.pathname === ROUTES.ORDER_HISTORY;
  const showPaymentMethodPage = location.pathname === ROUTES.PAYMENT_METHOD;
  const showPrivacySettings = location.pathname === ROUTES.PRIVACY_SETTINGS;
  const showAnnouncements = location.pathname === ROUTES.ANNOUNCEMENTS || location.pathname.startsWith('/announcements/');
  const showTermsOfService = location.pathname === ROUTES.TERMS;
  const showPrivacyPolicy = location.pathname === ROUTES.PRIVACY;

  // URL 기반 페이지 열기/닫기
  const openLoginPage = useCallback(() => navigate(ROUTES.LOGIN), [navigate]);
  const closeLoginPage = useCallback(() => goBack(ROUTES.HOME), [goBack]);

  const openEmailVerification = useCallback(() => navigate(ROUTES.VERIFY_EMAIL), [navigate]);
  const closeEmailVerification = useCallback(() => goBack(ROUTES.PROFILE), [goBack]);

  const openBirthdayVerification = useCallback(() => navigate(ROUTES.VERIFY_BIRTHDAY), [navigate]);
  const closeBirthdayVerification = useCallback(() => goBack(ROUTES.PROFILE), [goBack]);

  const openProfilePage = useCallback(() => navigate(ROUTES.PROFILE), [navigate]);
  const closeProfilePage = useCallback(() => goBack(ROUTES.HOME), [goBack]);

  const openOrderHistory = useCallback(() => navigate(ROUTES.ORDER_HISTORY), [navigate]);
  const closeOrderHistory = useCallback(() => goBack(ROUTES.HOME), [goBack]);

  const openPaymentMethodPage = useCallback(() => navigate(ROUTES.PAYMENT_METHOD), [navigate]);
  const closePaymentMethodPage = useCallback(() => goBack(ROUTES.HOME), [goBack]);

  const openPrivacySettings = useCallback(() => navigate(ROUTES.PRIVACY_SETTINGS), [navigate]);
  const closePrivacySettings = useCallback(() => goBack(ROUTES.ADMIN), [goBack]);

  const openAnnouncements = useCallback(() => navigate(ROUTES.ANNOUNCEMENTS), [navigate]);
  const closeAnnouncements = useCallback(() => goBack(ROUTES.HOME), [goBack]);

  const openTermsOfService = useCallback(() => navigate(ROUTES.TERMS), [navigate]);
  const closeTermsOfService = useCallback(() => goBack(ROUTES.HOME), [goBack]);

  const openPrivacyPolicy = useCallback(() => navigate(ROUTES.PRIVACY), [navigate]);
  const closePrivacyPolicy = useCallback(() => goBack(ROUTES.HOME), [goBack]);

  // 프로필에서 이메일 인증으로 이동
  const navigateFromProfileToEmailVerification = useCallback(() => {
    navigate(ROUTES.VERIFY_EMAIL);
  }, [navigate]);

  // 모든 페이지 모달 닫기 (상태 기반 모달만)
  const closeAllPageModals = useCallback(() => {
    closeAllModals();
  }, [closeAllModals]);

  // 호환성을 위한 no-op setter (URL 기반이므로 직접 변경 불가)
  const noOpSetter = useCallback(() => {
    logger.warn('이 setter는 더 이상 사용되지 않습니다. navigate를 사용하세요.');
  }, []);

  return {
    // Verification (URL 기반)
    showEmailVerification,
    setShowEmailVerification: noOpSetter,
    openEmailVerification,
    closeEmailVerification,
    showBirthdayVerification,
    setShowBirthdayVerification: noOpSetter,
    openBirthdayVerification,
    closeBirthdayVerification,

    // Profile & Order history (URL 기반)
    showProfilePage,
    setShowProfilePage: noOpSetter,
    openProfilePage,
    closeProfilePage,
    showOrderHistory,
    setShowOrderHistory: noOpSetter,
    openOrderHistory,
    closeOrderHistory,

    // Info pages (URL 기반)
    showAnnouncements,
    setShowAnnouncements: noOpSetter,
    openAnnouncements,
    closeAnnouncements,
    showTermsOfService,
    setShowTermsOfService: noOpSetter,
    openTermsOfService,
    closeTermsOfService,
    showPrivacyPolicy,
    setShowPrivacyPolicy: noOpSetter,
    openPrivacyPolicy,
    closePrivacyPolicy,

    // Settings (URL 기반 + 모달)
    showPaymentMethodPage,
    setShowPaymentMethodPage: noOpSetter,
    openPaymentMethodPage,
    closePaymentMethodPage,
    showPrivacySettings,
    setShowPrivacySettings: noOpSetter,
    openPrivacySettings,
    closePrivacySettings,
    showMapModal,
    setShowMapModal,
    openMapModal,
    closeMapModal,
    showSettingsModal,
    setShowSettingsModal,
    openSettingsModal,
    closeSettingsModal,
    showLoginPage,
    setShowLoginPage: noOpSetter,
    openLoginPage,
    closeLoginPage,
    showLogoutConfirm,
    setShowLogoutConfirm,
    openLogoutConfirm,
    closeLogoutConfirm,

    // Navigation
    navigateFromProfileToEmailVerification,

    // Actions
    closeAllPageModals,
  };
}
